import { isInSoftBlockMode } from 'constants/geoBlocking';
import { TOURNAMENT_CREATE_PAGE } from 'constants/pages';
import { pluralize } from 'utils/shared/pluralize';
import FilterIcon from 'svg/FilterIcon';
import Plus from 'svg/Plus';
import SafeAreaPage from 'layouts/SafeAreaPage';
import Button, { ButtonLink } from 'components/Button';
import DistanceDropdown from 'components/DiscoverPage/DistanceDropdown';
import FilterModal from 'components/DiscoverPage/FilterModal';
import { ExploreAllMarketPlacesTypesEnum } from 'components/DiscoverPage/props';
import MapToggleBar from 'components/Explore/MapToggleBar';
import Footer from 'components/Footer';
import GoogleMap from 'components/GoogleMap';
import TabSwitcher from 'components/TabSwitcher';
import TopNav from 'components/nav/TopNav';
import classNames from 'styles/utils/classNames';
import { TournamentsProps } from './props';
import useTournament from './useTournament';
import { TOURNAMENT_TYPE_OPTIONS } from './utils';

const ExploreTournaments: React.FC<TournamentsProps> = ({ tournaments }) => {
  const {
    TournamentFilters,
    TournamentList,
    tournamentMapProps,
    clearTournamentFilters,
    distance,
    setDistance,
    isModalOpen,
    openModal,
    closeModal,
    isOpenMap,
    handleToggleMap,
    discoverContainerRef,
    isDiscoverSectionInView,
    tournamentsLength,
    isPageLoaded,
    selectedType,
    setSelectedType,
  } = useTournament({
    tournaments,
  });

  return (
    <SafeAreaPage isHideSidebar shouldHideTabBar={isOpenMap}>
      <TopNav
        shouldHideNavigation={false}
        shouldShowMobile
        shouldLinkToAuthPage={false}
        shouldShowAdditionalLinks={false}
        topNavSearch={{
          isEnabled: true,
          isSecondRowOverlay: isOpenMap,
          isSecondRowScrollable: !isOpenMap,
          defaultExploreType: ExploreAllMarketPlacesTypesEnum.Tournaments,
          instant: true,
        }}
      />
      <div
        ref={discoverContainerRef}
        className={classNames(
          'relative grid w-full grid-cols-[minmax(0,1fr)_45%] grid-rows-[auto_1fr]',
          isOpenMap
            ? "[grid-template-areas:'header_header''list_map'] max-lg:h-[calc(100vh-theme(spacing.topnav-mobile))] max-lg:grid-rows-[1fr] max-lg:[grid-template-areas:'map_map']"
            : "h-full max-lg:[grid-template-areas:'header_header''list_list'] lg:min-h-[calc(100vh-theme(spacing.topnav-search))] lg:[grid-template-areas:'header_header''list_map']",
        )}
      >
        <div
          className={classNames(
            'sticky top-topnav-mobile z-10 flex flex-col gap-4 border-color-border-input-lightmode bg-color-bg-lightmode-primary p-4 dark:border-color-border-input-darkmode dark:bg-color-bg-darkmode-primary sm:border-b lg:top-topnav-search',
            '[grid-area:header]',
            isOpenMap && 'max-lg:hidden',
          )}
        >
          <div className="flex gap-6 max-lg:flex-col max-lg:gap-ds-md lg:items-center">
            <div className="flex items-center gap-3 max-lg:justify-between">
              <h3 className="typography-product-heading-desktop max-lg:typography-product-heading-mobile shrink-0 whitespace-nowrap">
                {pluralize({ count: tournamentsLength, singular: 'Tournament' })} in this area
              </h3>
            </div>

            <div className="hidden grow-[3] items-center gap-3 sm:flex">
              {/* Fixed-width wrapper: TabSwitcher is `w-full` internally, and classNames
                  does not merge Tailwind, so sizing has to come from the parent. */}
              <div className="w-[26.9375rem] shrink-0 max-xl:hidden">
                <TabSwitcher
                  options={TOURNAMENT_TYPE_OPTIONS}
                  selectedValue={selectedType}
                  onValueChange={setSelectedType}
                  keepIconColor
                />
              </div>
              <DistanceDropdown
                distance={distance}
                handleDistanceChange={(d) => setDistance(d)}
                activeTabValue={ExploreAllMarketPlacesTypesEnum.Tournaments}
              />
            </div>

            <div className="flex items-center gap-3 max-lg:hidden">
              <Button
                onClick={() => openModal()}
                variant="inverted"
                className="w-fit shrink-0 whitespace-nowrap"
                size="md"
                iconLeft={
                  <FilterIcon className="h-4 w-4 text-color-bg-lightmode-invert dark:text-color-bg-darkmode-invert" />
                }
              >
                More filters
              </Button>
            </div>
          </div>
        </div>

        {/* List section */}
        <div
          className={classNames(
            'flex min-w-0 flex-col overflow-auto',
            '[grid-area:list]',
            isOpenMap ? 'hidden lg:flex' : 'flex',
          )}
        >
          {/* TournamentList's sections supply their own top padding below this CTA */}
          <div className="px-4 pt-ds-xl sm:px-ds-xl">
            <ButtonLink
              href={TOURNAMENT_CREATE_PAGE}
              variant="secondary"
              size="md"
              className="mb-ds-lg w-fit shrink-0 whitespace-nowrap"
              iconLeft={<Plus className="h-4 w-4" />}
            >
              List your tournament
            </ButtonLink>
          </div>
          {TournamentList}
        </div>

        {/* Map section */}
        <div
          className={classNames(
            'sticky self-start [grid-area:map]',
            isOpenMap
              ? 'block max-lg:h-full lg:top-[calc(theme(spacing.topnav-search)+77px)] lg:h-[calc(100vh-theme(spacing.topnav-search)-77px)]'
              : 'max-lg:hidden lg:top-[calc(theme(spacing.topnav-search)+77px)] lg:h-[calc(100vh-theme(spacing.topnav-search)-77px)]',
          )}
        >
          <GoogleMap {...tournamentMapProps} softBlockPlaceholder={isInSoftBlockMode()} />
        </div>
      </div>

      <MapToggleBar
        isOpenMap={isOpenMap}
        onToggleMap={handleToggleMap}
        isInView={isDiscoverSectionInView}
        onOpenFilters={() => openModal()}
        isLoading={!isPageLoaded}
        loadingText="Finding tournaments near you..."
      />

      <FilterModal
        title="Filters"
        isOpen={isModalOpen}
        closeModal={closeModal}
        clearAll={clearTournamentFilters}
        closeButtonText={`Show ${tournamentsLength} Tournaments`}
      >
        {TournamentFilters}
      </FilterModal>

      <Footer />
    </SafeAreaPage>
  );
};

export default ExploreTournaments;
